import { type ByteSource, Reader, SliceSource } from './bytes';

const SECTOR = 2048;

export interface IsoEntry {
  /** Full path with `/` separators, original case, no version suffix (`;1`). */
  path: string;
  name: string;
  isDir: boolean;
  lba: number;
  size: number;
}

/**
 * Minimal ISO 9660 reader. Reads the primary volume descriptor and walks directories on demand.
 * Hybrid Mac/PC game discs carry "associated file" entries (resource forks) which are skipped.
 */
export class Iso9660 {
  private constructor(
    private readonly src: ByteSource,
    readonly volumeId: string,
    private readonly root: IsoEntry,
  ) {}

  static async open(src: ByteSource): Promise<Iso9660> {
    // Volume descriptors start at sector 16; find the primary (type 1).
    for (let s = 16; s < 32; s++) {
      const d = await src.read(s * SECTOR, SECTOR);
      if (d.length < SECTOR) break;
      const type = d[0];
      const ident = String.fromCharCode(...d.subarray(1, 6));
      if (ident !== 'CD001') break;
      if (type === 255) break;
      if (type === 1) {
        const volumeId = String.fromCharCode(...d.subarray(40, 72)).replace(/\0/g, " ").trim();
        const rootRec = new Reader(d, 156);
        const rec = rootRec.bytesN(34);
        const dv = new DataView(rec.buffer, rec.byteOffset, 34);
        const root: IsoEntry = { path: '', name: '', isDir: true, lba: dv.getUint32(2, true), size: dv.getUint32(10, true) };
        return new Iso9660(src, volumeId, root);
      }
    }
    throw new Error('Not an ISO 9660 image');
  }

  async list(dir: IsoEntry = this.root): Promise<IsoEntry[]> {
    const data = await this.src.read(dir.lba * SECTOR, dir.size);
    const out: IsoEntry[] = [];
    let i = 0;
    while (i < data.length) {
      const len = data[i];
      if (len === 0) {
        // Records never straddle sectors; skip to the next sector.
        i = (Math.floor(i / SECTOR) + 1) * SECTOR;
        continue;
      }
      const rec = data.subarray(i, i + len);
      i += len;
      const e = parseRecord(rec, dir.path);
      if (e) out.push(e);
    }
    return out;
  }

  /** Recursively lists every file (directories are descended, not returned). */
  async walk(dir: IsoEntry = this.root, maxDepth = 8): Promise<IsoEntry[]> {
    const out: IsoEntry[] = [];
    for (const e of await this.list(dir)) {
      if (e.isDir) {
        if (maxDepth > 0) out.push(...(await this.walk(e, maxDepth - 1)));
      } else out.push(e);
    }
    return out;
  }

  /** Case-insensitive lookup of a `/`-separated path. */
  async find(path: string): Promise<IsoEntry | null> {
    const parts = path.split(/[\\/]/).filter(Boolean);
    let dir = this.root;
    for (let p = 0; p < parts.length; p++) {
      const want = parts[p].toUpperCase();
      const hit = (await this.list(dir)).find((e) => e.name.toUpperCase() === want);
      if (!hit) return null;
      if (p === parts.length - 1) return hit;
      if (!hit.isDir) return null;
      dir = hit;
    }
    return dir;
  }

  open(entry: IsoEntry): ByteSource {
    return new SliceSource(this.src, entry.lba * SECTOR, entry.size);
  }
}

function parseRecord(rec: Uint8Array, parentPath: string): IsoEntry | null {
  if (rec.length < 34) return null;
  const r = new Reader(rec);
  const lba = r.view.getUint32(2, true);
  const size = r.view.getUint32(10, true);
  const flags = rec[25];
  const nameLen = rec[32];
  const raw = rec.subarray(33, 33 + nameLen);
  if (nameLen === 1 && (raw[0] === 0 || raw[0] === 1)) return null; // "." and ".."
  if (flags & 4) return null; // associated file (Mac resource fork)
  let name = String.fromCharCode(...raw).split(';')[0];
  if (name.endsWith('.')) name = name.slice(0, -1);
  const isDir = (flags & 2) !== 0;
  return { path: parentPath ? `${parentPath}/${name}` : name, name, isDir, lba, size };
}
